(ns dashboard.views.workbook-workspace.components.grid
  (:require [re-frame.core :as rf]
            [dashboard.components.popup-menu :as popup-menu]
            [dashboard.views.workbook-workspace.components.common :as components]
            [dashboard.views.workbook-workspace.components.context-menu :as context-menu]
            [dashboard.views.workbook-workspace.events :as events]
            [dashboard.views.workbook-workspace.subs :as subs]))

(def highlight-focus-color "#eef6fc")

(def column-options
  {:num "Id/Number"
   :content "Question"
   :response "Response"
   :additional-information "Comment"
   :depth "Depth"
   :analysis "Analysis"})

(defn grid-cell [{:keys [cell row-index selected-cells]}]
  [:td
   {:style           {:background
                      (when (and (= (:row-index selected-cells) row-index)
                                 (some #(or (and (:ref cell)
                                                 (:ref %)
                                                 (= (:ref cell) (:ref %)))
                                            (= (:col cell) (:col %)))
                                       (map (fn [[_column value]]
                                              value)
                                            (:columns selected-cells))))
                        highlight-focus-color)}

    :on-click        (fn [e]
                       (rf/dispatch [::events/workbook-workspace.grid.on-cell-click
                                     {:cell      cell
                                      :row       row-index
                                      :shift-key (.-shiftKey e)
                                      :meta-key  (.-metaKey e)
                                      :row-index row-index}]))
    :on-context-menu (fn [e]
                       (.preventDefault e)
                       (rf/dispatch
                         [::events/workbook-workspace.context-menu.open
                          {:row-index row-index
                           :cell-ref  (:ref cell)
                           :col-index (:col cell)
                           :screen-x  (.. e -pageX)
                           :screen-y  (.. e -pageY)}]))}
   (:content cell)])

(defn grid-row [{:keys [row row-index selected-cells is-hidden? is-selected? is-question?]}]
  [:tr {:style {:cursor     "pointer"
                :background (when is-selected? highlight-focus-color)
                :opacity    (when is-hidden? "0.3")}}
   [:td.px-2.has-text-weight-bold {:style    {:border-right "1px solid lightgrey"}
                                   :on-click #(rf/dispatch [::events/workbook-workspace.grid.on-row-click
                                                            {:shift-key (.. % -shiftKey)
                                                             :ctrl-key  (.. % -ctrlKey)
                                                             :row-index row-index}])
                                   :class    [(when is-question? "has-background-warning")]}
    [:p.has-text-centered row-index]]
   (for [cell row]
     [grid-cell {:key            (str "row_" row-index "_cell_" (:col cell))
                  :row-index      row-index
                  :cell           cell
                  :selected-cells selected-cells}])])

(defn sticky-grid-header [{:keys [questionnaire sheet  column-selection column-count]}]
  (let [used-cols        (into #{} (vals column-selection))
        used-col-indices (into #{} (keys column-selection))
        available-column-options (remove (fn [[k _]]
                                           (used-cols k))
                                         column-options)]
    [:thead {:style {:position         "sticky"
                     :background-color "rgba(22,22,22,0.1)"
                     :top              0}}
     [:tr
      [:td.px-1 {:style {:width "3rem"}}]
      (for [col (map #(if (contains? used-col-indices %)
                        [(get column-selection %) %]
                        %) (range column-count))]
        [:td {:key col}
         (if (coll? col)
           (let [[column-key column-index] col]
             [:button.button.is-small.is-danger.is-light
              {:on-click #(rf/dispatch
                            [::events/workbook-workspace.workbook.sheet.config.clear-value
                             {:workbook questionnaire :sheet sheet :k column-index}])
               :title "Remove selected column type"}
              (str (column-key column-options) " ✕")])
           (when (not= 0 (count available-column-options))
             [:div.select.is-small
              [:select
               {:key       (str sheet "_" col)
                :on-change #(rf/dispatch
                              [::events/workbook-workspace.workbook.sheet.config.set-value
                               {:workbook questionnaire
                                :sheet    sheet
                                :k        (keyword (.. % -target -value))
                                :v        col}])
                :title "Select the type of column to parse it as"}
               [:option "Select"]
               (map (fn [[id-key label]]
                      (into [:option] [(merge {:value (name id-key)} {:key (str sheet "_" label)}) label]))
                    available-column-options)]]))])]]))

(defn grid [{:keys [panel-max-height]}]
  (let [{:keys [sheet-id
                questionnaire
                is-loading?
                selected-rows
                selected-cells]} @(rf/subscribe [::subs/workbook-workspace.grid])
        {{:keys [hidden-rows
                 question-rows
                 depth-source]
          :as parsing-config}
         :parsing-config
         :keys [show-hidden-rows?]
         :as data} @(rf/subscribe [::subs/workbook-workspace.active-workbook])

        on-depth-config-change #(rf/dispatch [::events/workbook-workspace.active-sheet.depth.set-source
                                              (keyword (.. % -target -value))])]
    [:div.column.is-flex.is-flex-direction-column.p-0.workbook-content
     {:style {:max-height panel-max-height}}
     ;; Panel top bar
     ;; Render: workbook: name and toggle for show help
     [components/panel-top-bar
      (when (and questionnaire sheet-id)
        [:div.level-item.level-left.mx-3
         [:div.is-flex
          [:f> popup-menu/popup-menu
           {:trigger-class [:button :is-info :is-small :mr-2 :is-rounded]
            :trigger-content [:span.icon>i.fas.fa-solid.fa-info]
            :disabled false
            :content [:div.notification.is-info
                      {:style {:min-width "400px"
                               :padding-left "48px"}}
                      [:h6.title.is-6.is-centered
                       [:i.icon..button.is-rounded.fas.fa-info.mr-3]
                       "To Import Questions: "]
                      [:ul.bulleted
                       [:li "Identify at least the 'Question' and 'Response' columns."]
                       [:li "Select the rows you want to import."]
                       [:li "Use 'Actions' or right-click selected rows and choose 'set as question row'"]]]}]
          [:f> context-menu/action-menu]]
         [:div.control.mx-3
          (let [title (str "Show the rows that have been hidden via the context menu.\n\n"
                           "Hidden rows can help remove clutter from the sheet.")]
            [:label.checkbox
             {:checked   show-hidden-rows?
              :for       "show-hidden-rows"
              :title     title
              :on-change #(rf/dispatch [::events/workbook-workspace.active-sheet.toggle-hidden-row-visibility])}
             [:input.mr-3 {:type "checkbox"
                           :id "show-hidden-rows"
                           :title title}]
             "Show Hidden"])]

         [:p.has-text-bold.mx-3 "|"]
         [:p {:title (str "Choose how to choose the \"depth\" used to parse follow-up questions.\n\n"
                          "Depth means the number of rows that come after the top most parent question (root), leading to the row that the follow-up question is located on.")}
          "Question Depth: "]
         [:div.control.mx-3
          (let [title "Use the question number column, if number column is mapped to a sheet column"]
            [:label.radio {:for "from-number"
                           :title title}
             [:input.mr-3 {:on-change on-depth-config-change
                           :checked   (= :from-number depth-source)
                           :type      "radio"
                           :name      "depth"
                           :value     "from-number"
                           :id        "from-number"
                           :title     title}]
             "Question #"])]

         [:div.control.mx-3
          (let [title (str "Use the column chosen for question depth.\n\n"
                           "The column should contain a number that is then used as \"depth\", e.g. then number of rows that come after the start of the question hierarchy (root).")]
            [:label.radio {:for "from-column"
                           :title title}
             [:input.mr-3 {:on-change on-depth-config-change
                           :checked   (= :from-column depth-source)
                           :type      "radio"
                           :name      "depth"
                           :value     "from-column"
                           :id        "from-column"
                           :title     title}]
             "Column"])]

         [:div.control.mx-3
          (let [title "Use only depth defined in the browser component, if depth not available from question number or from a separate column."]
            [:label.radio {:for "explicit"
                           :title title}
             [:input.mr-3 {:on-change on-depth-config-change
                           :checked   (= :explicit depth-source)
                           :type      "radio"
                           :name      "depth"
                           :value     "explicit"
                           :id        "explicit"
                           :title     title}]
             "Explicit"])]])]

     ;; Container for the questionnaire render and help panel
     ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
     [:div.columns.m-0.is-flex.is-flex-direction-row-reverse
      {:style {:overflow "hidden"
               :height   "100%"}}

      ;; Render the selected sheet
      ;;;;;;;;;;;;;;;;;;;;;;;;
      [:div.column.is-flex.p-0
       {:style {:overflow "auto"}}
       (if (and questionnaire sheet-id (not is-loading?))
         [:table.table.is-striped.is-bordered
          {:style {;; set position as relative to enable
                   ;; sticky table header row
                   :position    "relative"
                   :user-select "none"
                   :width "100%"}}

          ;; Render table header.
          ;; Enable user select column "category" with select
          ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
          [sticky-grid-header {:questionnaire    questionnaire
                                :sheet            sheet-id
                                :column-selection (:columns parsing-config)
                                :column-count     (inc (apply max (map (fn [[_row-num columns]]
                                                                         (count columns))
                                                                       (:rows data))))}]

          [:tbody.p-5

           (for [[row-index row] (:rows data)]
             [grid-row {:key            (str "row_" row-index)
                         :row            row
                         :row-index      row-index
                         :is-hidden?     (contains? hidden-rows row-index)
                         :is-question?   (contains? question-rows row-index)
                         :is-selected?   (contains? selected-rows row-index)
                         :selected-cells selected-cells}])]]

         [:div.is-flex.is-flex-direction-column.is-flex-grow-1.is-justify-content-center.is-align-items-center
          {:style {:height           "100%"
                   :background-image "linear-gradient(135deg,  rgba(22,22,22,0.03), white, white, rgba(22,22,22,0.05))"}}
          [:img
           {:src   "/img/repliance-icon.svg"
            :width "auto"
            :class [(when is-loading? "animated-logo")]
            :style {:color "lightgrey" :height "500px" :padding "3rem" :opacity 0.2}}]
          (when is-loading?
            [:progress.progress.is-small.is-success {:max   "100"
                                                     :style {:width   "500px"
                                                             :opacity 0.2}} "15%"])])]]]))
